<template>
  <section class="">
    <div
      class="px-4 mx-auto max-w-3xl lg:px-0"
      :class="[
        paddingTop === 'reduced' ? 'pt-0' : 'pt-12 lg:pt-16',
        paddingBottom === 'reduced' ? 'pb-6 lg:pb-6' : 'pb-12 lg:pb-16'
      ]"
    >
      <div class="">
        <div
          class="flex flex-col items-start p-6 w-full rounded-2xl border border-[#E0D5C5]"
          :class="contentGap === 'large' ? 'gap-9' : 'gap-4'"
        >
          <div class="w-full">
            <nuxt-img
              v-if="heroImageSrc"
              :src="heroImageSrc"
              :alt="heroImageAlt"
              width="690"
              :height="heroImageHeight"
              format="webp"
              loading="lazy"
              sizes="xs:100vw lg:720px"
              class="object-cover w-full rounded-2xl"
              style="object-position: 100% 80%;"
              :style="{ height: `${heroImageHeight}px` }"
            />
            <div
              v-else
              class="w-full rounded-2xl bg-[#2A2A2A]"
              :style="{ height: `${heroImageHeight}px` }"
              aria-hidden="true"
            />
          </div>

          <div
            v-if="title || introContent"
            class="flex flex-col gap-2.5 w-full"
          >
            <h2
              v-if="title"
              class="font-ww-bold text-[#4A4A4A]"
              :class="titleSize === 'large' ? 'text-34 leading-42' : 'text-22 leading-27'"
              v-html="title"
            />

            <div
              v-if="introContent"
              class="text-17 leading-6 text-[#4A4A4A] font-ww-regular [&_p+p]:mt-5 [&_strong]:font-ww-bold [&_sup]:text-xs"
              v-html="introContent"
            />
          </div>

          <template
            v-for="(section, index) in sections"
            :key="index"
          >
            <div
              v-if="section.type === 'divider'"
              class="h-px w-full bg-[#E0D5C5]"
              aria-hidden="true"
            />

            <div
              v-else-if="section.type === 'split'"
              class="flex flex-col gap-6 w-full lg:flex-row lg:items-start"
              :class="[
                section.imagePosition === 'right' ? 'lg:flex-row-reverse lg:gap-16' : 'lg:gap-8'
              ]"
            >
              <div class="relative mx-auto w-full max-w-xs shrink-0 lg:mx-0">
                <nuxt-img
                  v-if="section.imageSrc"
                  :src="section.imageSrc"
                  :alt="section.imageAlt"
                  :width="section.imageWidth || 281"
                  :height="section.imageHeight || 350"
                  format="webp"
                  loading="lazy"
                  sizes="xs:100vw sm:320px lg:320px"
                  class="object-cover w-full rounded-2xl"
                  :style="{ height: `${section.imageHeight || 350}px` }"
                />
                <div
                  v-else
                  class="w-full rounded-2xl bg-[#2A2A2A]"
                  :style="{ height: `${section.imageHeight || 350}px` }"
                  aria-hidden="true"
                />
              </div>

              <div class="flex flex-col flex-1 gap-2.5 min-w-0">
                <h3
                  v-if="section.title"
                  class="text-22 font-bold leading-27 font-ww-bold text-[#4A4A4A]"
                  v-html="section.title"
                />

                <div
                  v-if="section.content"
                  class="text-17 leading-6 text-[#4A4A4A] font-ww-regular [&_p+p]:mt-5 [&_strong]:font-ww-bold [&_sup]:text-xs"
                  v-html="section.content"
                />
              </div>
            </div>

            <div
              v-else-if="section.type === 'tags'"
              class="flex flex-col gap-4 w-full"
            >
              <h3
                v-if="section.title"
                class="text-22 font-bold leading-27 font-ww-bold text-[#4A4A4A]"
                v-html="section.title"
              />

              <div
                v-if="section.content"
                class="text-17 leading-6 text-[#4A4A4A] font-ww-regular [&_p+p]:mt-5 [&_strong]:font-ww-bold [&_sup]:text-xs"
                v-html="section.content"
              />

              <div
                v-if="getTags(section).length"
                class="flex flex-wrap gap-4"
              >
                <div
                  v-for="(tag, tagIndex) in getTags(section)"
                  :key="tagIndex"
                  class="flex gap-2.5 items-start px-4 py-2.5 rounded-md border border-[#E0D5C5] bg-[#FAF6F0]"
                >
                  <span class="text-sm leading-6 text-[#F1574B] font-ww-regular shrink-0">✓</span>
                  <span
                    class="text-sm leading-26 text-[#4A4A4A] font-ww-regular whitespace-nowrap"
                    v-html="tag.text"
                  />
                </div>
              </div>
            </div>
          </template>

          <div
            v-if="columns.length"
            class="grid grid-cols-1 gap-9 mt-4 w-full lg:grid-cols-2"
          >
            <article
              v-for="(column, columnIndex) in columns"
              :key="columnIndex"
              class="flex flex-col gap-4"
            >
              <div class="w-full h-[202px]">
                <nuxt-img
                  v-if="column.imageSrc"
                  :src="column.imageSrc"
                  :alt="column.imageAlt"
                  width="358"
                  height="202"
                  format="webp"
                  loading="lazy"
                  sizes="xs:100vw lg:342px"
                  class="object-cover w-full h-[202px] rounded-2xl object-top"
                />
                <div
                  v-else
                  class="w-full h-[202px] rounded-2xl bg-[#2A2A2A]"
                  aria-hidden="true"
                />
              </div>

              <h3
                v-if="column.title"
                class="text-22 font-bold leading-27 font-ww-bold text-[#4A4A4A]"
                v-html="column.title"
              />

              <div
                v-if="column.content"
                class="text-17 leading-6 text-[#4A4A4A] font-ww-regular [&_p+p]:mt-5 [&_ul]:list-disc [&_ul]:ml-6 [&_u]:underline [&_strong]:font-ww-bold [&_sup]:text-xs"
                v-html="column.content"
              />
            </article>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
const muscleBuildingSectionsDefault = [
  { type: 'divider' },
  {
    type: 'split',
    title: 'Resistance Training',
    content: `<p>Resistance training is a must when building muscle as this type of training causes microscopic tears in muscle fibers through both stress and tension.</p>
<p>These tears, or damaged muscle fibers, are then repaired by the body through what is known as protein synthesis, a process where amino acids aid in repairing the torn tissue to build new.</p>
<p>Over time, the tears, and subsequently their repair, serve to increase muscle mass, bringing with this increase the added benefits discussed above.<sup>23</sup></p>`,
    imageSrc: '/assets/images/muscle-building-resistance-training.jpg',
    imageAlt: 'Man performing suspension training',
    imagePosition: 'left',
    imageHeight: 350
  },
  { type: 'divider' },
  {
    type: 'tags',
    title: 'Protein Prioritization',
    content: `<p>Building muscle is complex.</p>
<p>Translation? You need to do more than strength or resistance training to help your body build it.</p>
<p>And, this is where diet comes in.</p>
<p>When seeking to build muscle, consuming 1 pound of protein per 1 pound of body weight is recommended as this protein provides the amino acids needed for protein synthesis, the process we discussed above which repairs torn muscle fibers to build muscle.<sup>24</sup></p>
<p>Some great sources of protein include:</p>`,
    tags: [
      { text: 'Eggs' },
      { text: 'Wild caught salmon' },
      { text: 'Chicken breast' },
      { text: 'Turkey breast' },
      { text: 'Tuna' },
      { text: 'Plain greek yogurt' },
      { text: 'Lean beef' },
      { text: 'Shrimp' },
      { text: 'Cottage cheese' },
      { text: 'Beans' },
      { text: 'Protein powders' },
      { text: 'Pork' },
      { text: 'Bison' }
    ]
  },
  { type: 'divider' },
  {
    type: 'split',
    title: 'Creatine',
    content: `<p>Muscle loss is a reality as we age, and while prioritizing protein and actively seeking to build muscle is the remedy for this, supplementing your diet with creatine can serve you by helping to mitigate muscle loss.</p>
<p>Creatine helps to combat muscle loss by improving bone density, strength, and muscle mass when taken in conjunction with regular resistance training.</p>
<p>So, creatine helps to ensure the work you're doing (muscle building) doesn't go to waste, even helping your body benefit fully from your training.<sup>25</sup></p>`,
    imageSrc: '/assets/images/muscle-building-creatine.jpg',
    imageAlt: 'Creatine powder with scoop',
    imagePosition: 'right',
    imageWidth: 320,
    imageHeight: 446
  }
]
</script>

<script setup>
const getTags = (section) => {
  if (section.tags?.length) {
    return section.tags
  }

  if (section.tagsText) {
    return section.tagsText
      .split('\n')
      .map((text) => text.trim())
      .filter(Boolean)
      .map((text) => ({ text }))
  }

  return []
}

defineProps({
  heroImageSrc: {
    type: String,
    default: '/assets/images/muscle-building-hero.jpg'
  },
  heroImageAlt: {
    type: String,
    default: 'Man lifting weights in a gym'
  },
  heroImageHeight: {
    type: Number,
    default: 285
  },
  title: {
    type: String,
    default: 'Muscle Building'
  },
  titleSize: {
    type: String,
    default: 'large',
    validator: (value) => ['large', 'medium'].includes(value)
  },
  introContent: {
    type: String,
    default: `<p>Gym sessions aren't just a hobby, and building muscle isn't just about appearance.</p>
<p>Building muscle mass is crucial for healthy aging, protecting your metabolic health, improving insulin sensitivity, supporting heart health, improving bone health, and increasing longevity.</p>
<p>When seeking to build muscle, focusing on resistance training, protein, and adding in creatine to your diet can make a huge difference.</p>`
  },
  sections: {
    type: Array,
    default: () => muscleBuildingSectionsDefault
  },
  columns: {
    type: Array,
    default: () => []
  },
  contentGap: {
    type: String,
    default: 'large',
    validator: (value) => ['large', 'medium'].includes(value)
  },
  paddingTop: {
    type: String,
    default: 'reduced',
    validator: (value) => ['default', 'reduced'].includes(value)
  },
  paddingBottom: {
    type: String,
    default: 'reduced',
    validator: (value) => ['default', 'reduced'].includes(value)
  }
})

defineBuilderComponent({
  friendlyName: 'Content - Guide Topic Card Image',
  inputs: [
    {
      name: 'heroImageSrc',
      type: 'file',
      allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp'],
      defaultValue: '/assets/images/muscle-building-hero.jpg'
    },
    {
      name: 'heroImageAlt',
      type: 'text',
      defaultValue: 'Man lifting weights in a gym'
    },
    {
      name: 'heroImageHeight',
      type: 'number',
      defaultValue: 285
    },
    {
      name: 'title',
      type: 'richText',
      defaultValue: 'Muscle Building'
    },
    {
      name: 'titleSize',
      type: 'text',
      enum: ['large', 'medium'],
      defaultValue: 'large'
    },
    {
      name: 'introContent',
      type: 'richText',
      defaultValue: `<p>Gym sessions aren't just a hobby, and building muscle isn't just about appearance.</p>
<p>Building muscle mass is crucial for healthy aging, protecting your metabolic health, improving insulin sensitivity, supporting heart health, improving bone health, and increasing longevity.</p>
<p>When seeking to build muscle, focusing on resistance training, protein, and adding in creatine to your diet can make a huge difference.</p>`
    },
    {
      name: 'sections',
      type: 'list',
      defaultValue: [
        { type: 'divider' },
        {
          type: 'split',
          title: 'Resistance Training',
          content: `<p>Resistance training is a must when building muscle as this type of training causes microscopic tears in muscle fibers through both stress and tension.</p>
<p>These tears, or damaged muscle fibers, are then repaired by the body through what is known as protein synthesis, a process where amino acids aid in repairing the torn tissue to build new.</p>
<p>Over time, the tears, and subsequently their repair, serve to increase muscle mass, bringing with this increase the added benefits discussed above.<sup>23</sup></p>`,
          imageSrc: '/assets/images/muscle-building-resistance-training.jpg',
          imageAlt: 'Man performing suspension training',
          imagePosition: 'left',
          imageHeight: 350
        },
        { type: 'divider' },
        {
          type: 'tags',
          title: 'Protein Prioritization',
          content: `<p>Building muscle is complex.</p>
<p>Translation? You need to do more than strength or resistance training to help your body build it.</p>
<p>And, this is where diet comes in.</p>
<p>When seeking to build muscle, consuming 1 pound of protein per 1 pound of body weight is recommended as this protein provides the amino acids needed for protein synthesis, the process we discussed above which repairs torn muscle fibers to build muscle.<sup>24</sup></p>
<p>Some great sources of protein include:</p>`,
          tagsText: `Eggs
Wild caught salmon
Chicken breast
Turkey breast
Tuna
Plain greek yogurt
Lean beef
Shrimp
Cottage cheese
Beans
Protein powders
Pork
Bison`
        },
        { type: 'divider' },
        {
          type: 'split',
          title: 'Creatine',
          content: `<p>Muscle loss is a reality as we age, and while prioritizing protein and actively seeking to build muscle is the remedy for this, supplementing your diet with creatine can serve you by helping to mitigate muscle loss.</p>
<p>Creatine helps to combat muscle loss by improving bone density, strength, and muscle mass when taken in conjunction with regular resistance training.</p>
<p>So, creatine helps to ensure the work you're doing (muscle building) doesn't go to waste, even helping your body benefit fully from your training.<sup>25</sup></p>`,
          imageSrc: '/assets/images/muscle-building-creatine.jpg',
          imageAlt: 'Creatine powder with scoop',
          imagePosition: 'right',
          imageWidth: 320,
          imageHeight: 446
        }
      ],
      subFields: [
        {
          name: 'type',
          type: 'text',
          enum: ['divider', 'split', 'tags'],
          defaultValue: 'split'
        },
        {
          name: 'title',
          type: 'text',
          defaultValue: ''
        },
        {
          name: 'content',
          type: 'richText',
          defaultValue: ''
        },
        {
          name: 'imageSrc',
          type: 'file',
          allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp'],
          defaultValue: ''
        },
        {
          name: 'imageAlt',
          type: 'text',
          defaultValue: ''
        },
        {
          name: 'imagePosition',
          type: 'text',
          enum: ['left', 'right'],
          defaultValue: 'left'
        },
        {
          name: 'imageHeight',
          type: 'number',
          defaultValue: 350
        },
        {
          name: 'tagsText',
          type: 'longText',
          defaultValue: '',
          helperText: 'For tags sections: one tag per line'
        }
      ]
    },
    {
      name: 'columns',
      type: 'list',
      defaultValue: [],
      subFields: [
        {
          name: 'imageSrc',
          type: 'file',
          allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp'],
          defaultValue: ''
        },
        {
          name: 'imageAlt',
          type: 'text',
          defaultValue: ''
        },
        {
          name: 'title',
          type: 'text',
          defaultValue: ''
        },
        {
          name: 'content',
          type: 'richText',
          defaultValue: ''
        }
      ]
    },
    {
      name: 'contentGap',
      type: 'text',
      enum: ['large', 'medium'],
      defaultValue: 'large'
    },
    {
      name: 'paddingTop',
      type: 'text',
      enum: ['default', 'reduced'],
      defaultValue: 'reduced'
    },
    {
      name: 'paddingBottom',
      type: 'text',
      enum: ['default', 'reduced'],
      defaultValue: 'reduced'
    }
  ]
})
</script>
